import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserTier, PermissionResult, UsageLimits } from '../types/permissions';
import { API_BASE_URL } from '../config';

// Constants matching backend
const LIMITS = {
  unregistered: {
    chats: 5,
    fares: 2,
  },
  google_auth: {
    chats: 2, // per hour (reduced from 10)
    fares: 2,  // per hour
  },
  whatsapp_verified: {
    chats: Infinity,
    fares: Infinity,
  },
};

const STORAGE_KEY = 'hey-sl-limits';

export function usePermissions() {
  const { authState } = useAuth();
  
  // Determine user tier
  // IMPORTANT: Depend on the entire user object to catch all changes
  const tier = useMemo<UserTier>(() => {
    const whatsappNumber = authState.user?.whatsappNumberE164;
    const telegramNumber = authState.user?.telegramNumberE164;
    const hasWhatsApp = !!whatsappNumber;
    const hasTelegram = !!telegramNumber;
    const isWhatsAppLocal = whatsappNumber?.startsWith('+94');
    const isTelegramLocal = telegramNumber?.startsWith('+94');
    const userId = authState.user?.userId;
    
    if (!authState.user) {
      return 'unregistered';
    }
    
    // Check Telegram verification first (matches backend priority)
    if (hasTelegram) {
      if (isTelegramLocal) {
        return 'google_auth';
      }
      return 'whatsapp_verified';
    }
    
    // Check WhatsApp verification
    if (hasWhatsApp) {
      if (isWhatsAppLocal) {
        return 'google_auth';
      }
      return 'whatsapp_verified';
    }
    
    return 'google_auth';
  }, [authState.user, authState.isAuthenticated]); // Changed to watch entire user object
  
  // Log tier changes for debugging
  useEffect(() => {
  }, [tier]);
  
  // Client-side tracking for unregistered users
  const [localLimits, setLocalLimits] = useState<UsageLimits>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          chats: parsed.chats || 0,
          fares: parsed.fares || 0,
          lastReset: parsed.lastReset || Date.now(),
        };
      }
      } catch (error) {
        // Error loading local limits
      }
      return { chats: 0, fares: 0, lastReset: Date.now() };
  });
  
  // Save local limits to localStorage
  useEffect(() => {
    if (tier === 'unregistered') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(localLimits));
      } catch (error) {
        // Error saving local limits
      }
    }
  }, [localLimits, tier]);
  
  // Clear local limits when user signs in
  useEffect(() => {
    if (tier !== 'unregistered') {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // Error clearing local limits
      }
    }
  }, [tier]);
  
  /**
   * Check if user can send a chat message
   */
  const canChat = useCallback((): PermissionResult => {
    if (tier === 'whatsapp_verified') {
      return { allowed: true, unlimited: true };
    }
    
    if (tier === 'unregistered') {
      const remaining = Math.max(0, LIMITS.unregistered.chats - localLimits.chats);
      return { 
        allowed: localLimits.chats < LIMITS.unregistered.chats,
        remaining,
      };
    }
    
    // For google_auth, server will check
    return { allowed: true, needsServerCheck: true };
  }, [tier, localLimits.chats]);
  
  /**
   * Check if user can calculate fare
   */
  const canCalculateFare = useCallback((isQuickRoute: boolean = false): PermissionResult => {
    // Quick routes are always free
    if (isQuickRoute) {
      return { allowed: true };
    }
    
    if (tier === 'whatsapp_verified') {
      return { allowed: true, unlimited: true };
    }
    
    if (tier === 'unregistered') {
      const remaining = Math.max(0, LIMITS.unregistered.fares - localLimits.fares);
      const allowed = localLimits.fares < LIMITS.unregistered.fares;
      return { 
        allowed,
        remaining,
      };
    }
    
    // For google_auth, server will check
    return { allowed: true, needsServerCheck: true };
  }, [tier, localLimits.fares]);
  
  /**
   * Check if user can save places
   */
  const canSave = useCallback((): PermissionResult => {
    return { allowed: tier !== 'unregistered' };
  }, [tier]);
  
  /**
   * Check if user can optimize trips
   */
  const canOptimizeTrip = useCallback((): PermissionResult => {
    const allowed = tier === 'whatsapp_verified';
    return { allowed };
  }, [tier]);
  
  /**
   * Record a chat usage (for unregistered users only)
   */
  const recordChatUsage = useCallback(() => {
    if (tier === 'unregistered') {
      setLocalLimits(prev => ({
        ...prev,
        chats: prev.chats + 1,
      }));
    }
    // For registered users, backend handles this
  }, [tier]);
  
  /**
   * Record a fare usage
   * For unregistered: Update localStorage
   * For registered: Call backend to record in Firestore
   */
  const recordFareUsage = useCallback(async (pickup?: any, destination?: any) => {
    if (tier === 'unregistered') {
      setLocalLimits(prev => ({
        ...prev,
        fares: prev.fares + 1,
      }));
    } else if (authState.user?.userId && pickup && destination) {
      // For registered users, call backend
      try {
        const response = await fetch(`${API_BASE_URL}/api/fare-limit/record`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: authState.user.userId,
            pickup,
            destination,
          }),
        });
        
        if (!response.ok) {
          // Failed to record fare usage on backend
        }
      } catch (error) {
        // Error recording fare usage
      }
    }
  }, [tier, localLimits.fares, authState.user?.userId]);
  
  /**
   * Check fare limit on server (for registered users)
   * @param pickup - Pickup location
   * @param destination - Destination location
   * @param isQuickRoute - Whether this is a quick route (always allowed)
   * @returns Promise with permission result
   */
  const checkFareLimitOnServer = useCallback(async (
    pickup: any, 
    destination: any, 
    isQuickRoute: boolean
  ): Promise<PermissionResult> => {
    if (!authState.user?.userId) {
      return { allowed: true };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/fare-limit/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: authState.user.userId,
          pickup,
          destination,
          isQuickRoute,
        }),
      });

      const data = await response.json();

      if (response.status === 429) {
        // Rate limit exceeded
        return {
          allowed: false,
          waitMinutes: data.waitMinutes,
        };
      }

      return {
        allowed: data.allowed,
        remaining: data.remaining,
      };
    } catch (error) {
      // On error, allow the request
      return { allowed: true };
    }
  }, [authState.user?.userId]);

  /**
   * Reset local limits (for testing)
   */
  const resetLocalLimits = useCallback(() => {
    setLocalLimits({ chats: 0, fares: 0, lastReset: Date.now() });
    localStorage.removeItem(STORAGE_KEY);
  }, []);
  
  return {
    tier,
    canChat,
    canCalculateFare,
    canSave,
    canOptimizeTrip,
    recordChatUsage,
    recordFareUsage,
    checkFareLimitOnServer, // NEW: For Services component to call before fare calculation
    resetLocalLimits,
    localLimits: tier === 'unregistered' ? localLimits : null,
  };
}

